import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { I18n } from '@kineticdata/react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { List } from 'immutable';

export const TileCarousel = connect(state => ({
  layoutSize: state.layout.size,
}))(props => {
  const {
    component: Tile,
    getTileProps = () => ({}),
    data,
    disableAnimation,
    tag: Tag = 'section',
    interval = 0,
  } = props;
  const records = List(data || []);
  const slideshowInterval = interval ? Math.max(interval, 3000) : null;

  // Number of tiles show show at a time
  const pageSize = props.hero
    ? 1
    : props.layoutSize === 'small'
      ? 2
      : props.layoutSize !== 'xlarge'
        ? 3
        : 4;
  // Number of pages
  const maxPage = records.size > 0 ? Math.ceil(records.size / pageSize) - 1 : 0;
  // State for current page
  const [page, setPage] = useState(0);
  // Current tiles to show
  const pageRecords = records
    .slice(page * pageSize, (page + 1) * pageSize)
    .map((record, i, list) => (
      <Tile
        {...getTileProps(record)}
        record={record}
        components={{ Link }}
        className={
          i + 1 === list.size && i + 1 < pageSize
            ? `tile-spacer-${pageSize - i - 1}`
            : ''
        }
      />
    ));

  // State for slideshow
  const [slideshowActive, setSlideshowActive] = useState(
    !!slideshowInterval && maxPage > 0,
  );
  const [slideshowId, setSlideshowId] = useState(undefined);
  const [slideshowNow, setSlideshowNow] = useState(new Date().getTime());
  const [slideshowLast, setSlideshowLast] = useState(new Date().getTime());
  const handleSlideshowToggle = () =>
    setSlideshowActive(a => (!!slideshowInterval ? !a : a));

  // State for previous tiles
  const previousPageRecords = useRef();
  // Handler for going to previous page
  const handlePreviousPage = useCallback(
    () => {
      setPage(page > 0 ? page - 1 : maxPage);
      setTransitioning(-1);
      previousPageRecords.current = pageRecords;
      setSlideshowLast(new Date().getTime());
    },
    [page, maxPage, pageRecords],
  );
  // Handler for going to next page
  const handleNextPage = useCallback(
    () => {
      setPage(page < maxPage ? page + 1 : 0);
      setTransitioning(1);
      previousPageRecords.current = pageRecords;
      setSlideshowLast(new Date().getTime());
    },
    [page, maxPage, pageRecords],
  );
  // State for handling animations when transitioning between pages
  const [transitioning, setTransitioning] = useState(0);
  const [animating, setAnimating] = useState(0);
  // State for touch interaction
  const [touchPosition, setTouchPosition] = useState(null);
  // Touch handlers for swiping between pages
  const handleTouchStart = e => {
    if (maxPage > 0 && e.touches.length === 1) {
      setTouchPosition([0, e.touches[0].clientX]);
    }
  };
  const handleTouchMove = e => {
    if (touchPosition && e.touches.length === 1) {
      setTouchPosition([
        touchPosition[0] + (touchPosition[1] - e.touches[0].clientX),
        e.touches[0].clientX,
      ]);
    }
  };
  const handleTouchEnd = e => {
    if (touchPosition && e.touches.length === 0) {
      const diff =
        touchPosition[0] + (touchPosition[1] - e.changedTouches[0].clientX);
      if (diff > 5) {
        handleNextPage();
      } else if (diff < -5) {
        handlePreviousPage();
      }
      setTouchPosition(null);
    }
  };

  // Clear the interval on component unmount
  useEffect(
    () => () => {
      window.clearInterval(slideshowId);
    },
    [slideshowId],
  );

  // Start and stop interval on mount and when paused/resumed
  useEffect(
    () => {
      if (slideshowActive) {
        setSlideshowLast(new Date().getTime());
        setSlideshowId(id => {
          window.clearInterval(id);
          return window.setInterval(() => {
            setSlideshowNow(new Date().getTime());
          }, 1000);
        });
      } else {
        setSlideshowId(id => window.clearInterval(id));
      }
    },
    [slideshowActive],
  );

  // If slideshow is active and page hasn't changed within the interval, trigger next page
  useEffect(
    () => {
      if (slideshowActive) {
        if (slideshowNow - slideshowLast >= slideshowInterval) {
          handleNextPage();
        }
      }
    },
    [
      slideshowActive,
      slideshowNow,
      slideshowLast,
      handleNextPage,
      slideshowInterval,
    ],
  );

  // If maxPage changes, check if animation should be active or not
  useEffect(
    () => {
      setSlideshowActive(!!slideshowInterval && maxPage > 0);
    },
    [slideshowInterval, maxPage],
  );

  // If maxPage changes and current page is greater than maxPage, reset page
  useEffect(
    () => {
      if (page > maxPage) {
        setPage(maxPage);
      }
    },
    [page, maxPage],
  );

  // If page transition has been triggered, start animation
  useEffect(
    () => {
      if (!disableAnimation && transitioning !== 0) {
        setAnimating(1);
      }
    },
    [disableAnimation, transitioning],
  );

  // If animation has been triggered, end animation after a 300 milliseconds
  useEffect(
    () => {
      if (!disableAnimation && animating !== 0) {
        window.setTimeout(() => {
          setTransitioning(0);
          setAnimating(0);
        }, 300);
      }
    },
    [disableAnimation, animating],
  );

  const pagingControls =
    maxPage > 0 ? (
      <div className="controls">
        {new Array(maxPage + 1).fill().map((_, i) => (
          <span
            key={`page-indicator-${i}`}
            className={classNames('page-indicator', {
              active: i === page,
            })}
            aria-label={`Page ${i + 1} ${i === page ? 'Active' : 'Inactive'}`}
          />
        ))}
        <button
          className="btn btn-link pr-2"
          onClick={handlePreviousPage}
          disabled={animating}
          aria-label={`${props.label}: Previous Page`}
        >
          <span className="fa fa-md fa-angle-left" />
        </button>
        {slideshowInterval && (
          <button
            className="btn btn-link px-2"
            onClick={handleSlideshowToggle}
            aria-label={slideshowActive ? 'Pause' : 'Play'}
          >
            <span
              className={classNames('fa fa-md', {
                'fa-pause': slideshowActive,
                'fa-play': !slideshowActive,
              })}
            />
          </button>
        )}
        <button
          className="btn btn-link pl-2"
          onClick={handleNextPage}
          disabled={animating}
          aria-label={`${props.label}: Next Page`}
        >
          <span className="fa fa-md fa-angle-right" />
        </button>
      </div>
    ) : null;

  return (
    records.size > 0 && (
      <Tag
        className={classNames('tile-carousel__wrapper', props.className, {
          'tile-carousel--hero': props.hero,
        })}
      >
        {(!props.hero || props.layoutSize === 'small') && (
          <div className="section__title">
            <span className="title">
              {props.labelLink ? (
                <Link to={props.labelLink}>
                  <I18n>{props.label}</I18n>
                  <span className="fa fa-arrow-right" />
                </Link>
              ) : (
                <I18n>{props.label}</I18n>
              )}
            </span>
            {pagingControls}
          </div>
        )}
        <div className="tile-carousel">
          {props.hero &&
            props.layoutSize !== 'small' && (
              <div className="title">
                {props.labelLink ? (
                  <Link to={props.labelLink}>
                    <I18n>{props.label}</I18n>
                    <span className="fa fa-md fa-arrow-right" />
                  </Link>
                ) : (
                  <I18n>{props.label}</I18n>
                )}
              </div>
            )}
          <div
            className={classNames('cards cards--row', {
              'transition-previous': !disableAnimation && transitioning === -1,
              'transition-next': !disableAnimation && transitioning === 1,
              animating: !disableAnimation && animating,
              'swipe-previous': touchPosition && touchPosition[0] < -5,
              'swipe-next': touchPosition && touchPosition[0] > 5,
            })}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {transitioning === 1 && previousPageRecords.current}
            {pageRecords}
            {transitioning === -1 && previousPageRecords.current}
          </div>
          {props.hero && props.layoutSize !== 'small' && pagingControls}
        </div>
      </Tag>
    )
  );
});
